<!--
  CounselorManage.vue
  상담톡 관리 페이지

  주요 기능:
  - 상담톡 신청 목록 조회
  - 상담톡 상태 변경 (승인/거절/해지)
  - 발신프로필키 입력
-->
<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>

    <!-- 페이지 헤더 영역 -->
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <div class="title-row">
            <h1 class="fw-bold page-title">
              상담톡 관리
            </h1>
            <div class="header-actions">
              <a-button
                type="default"
                :loading="state.tableLoading"
                @click="handleRefresh"
                class="refresh-button"
              >
                <i class="fa-solid fa-refresh"></i>
                조회
              </a-button>
            </div>
          </div>
          <span class="pageDesc">카카오 상담톡 연결 신청을 관리합니다.</span>
        </div>
      </div>
    </div>

    <!-- 콘텐츠 영역 -->
    <div class="px-0 pb-2">
      <div class="card content-wrap-card">
        <!-- 검색/필터 영역 -->
        <CounselorSearchFilter
          v-model:keyword="searchParams.keyword"
          v-model:status="searchParams.status"
          @search="handleSearch"
        />

        <!-- 테이블 컴포넌트 -->
        <CounselorTable
          :data-list="counselorList"
          :loading="state.tableLoading"
          :pagination="pagination"
          @channel-click="handleChannelClick"
          @table-change="handleTableChange"
        />
      </div>
    </div>

    <!-- 상세/수정 모달 -->
    <CounselorModal
      :open="state.modalVisible"
      :loading="state.modalLoading"
      :counselor-data="currentCounselorData"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
    />
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import CounselorApi from './api';
import CounselorSearchFilter from './components/CounselorSearchFilter.vue';
import CounselorTable from './components/CounselorTable.vue';
import CounselorModal from './components/CounselorModal.vue';

export default {
  name: "CounselorManage",
  components: {
    CounselorSearchFilter,
    CounselorTable,
    CounselorModal
  },
  setup() {
    // 반응형 상태 정의
    const state = reactive({
      tableLoading: false,
      modalVisible: false,
      modalLoading: false,
    });

    // 검색 파라미터
    const searchParams = reactive({
      keyword: '',
      status: '',
    });

    // 데이터 상태
    const counselorList = ref([]);
    const currentCounselorData = ref({});

    // 페이지네이션
    const pagination = reactive({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`
    });

    // 공통 에러 처리 함수
    const handleError = (error, defaultMessage) => {
      console.error('Error occurred:', error);

      let errorMessage = defaultMessage;

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);
      return errorMessage;
    };

    // 상담톡 목록 로드
    const loadCounselors = async () => {
      state.tableLoading = true;

      try {
        const params = {
          page: pagination.current,
          limit: pagination.pageSize,
          keyword: searchParams.keyword || undefined,
          status: searchParams.status || undefined,
          sortBy: 'requestedAt',
          sortOrder: 'desc',
        };

        const response = await CounselorApi.getList(params);

        const { data } = response;
        if (!data) {
          counselorList.value = [];
          pagination.total = 0;
          return;
        }

        if (data.docs && Array.isArray(data.docs)) {
          counselorList.value = data.docs.map((item, index) => ({
            ...item,
            index: (pagination.current - 1) * pagination.pageSize + index + 1,
          }));
          pagination.total = data.totalDocs || 0;
          pagination.current = data.page || 1;
          pagination.pageSize = data.limit || 10;
        } else {
          counselorList.value = [];
          pagination.total = 0;
        }
      } catch (error) {
        handleError(error, '상담톡 목록을 불러오는 중 오류가 발생했습니다.');
        counselorList.value = [];
        pagination.total = 0;
      } finally {
        state.tableLoading = false;
      }
    };

    // 채널 클릭 핸들러 (상세 모달 열기)
    const handleChannelClick = async (record, channelId) => {
      try {
        state.modalLoading = true;
        state.modalVisible = true;

        const response = await CounselorApi.getDetail(record._id, channelId);
        currentCounselorData.value = response.data;
      } catch (error) {
        handleError(error, '상담톡 상세 정보를 불러오는 중 오류가 발생했습니다.');
        state.modalVisible = false;
      } finally {
        state.modalLoading = false;
      }
    };

    // 모달 확인 처리 (상태 및 발신프로필키 저장)
    const handleModalOk = async (formData) => {
      state.modalLoading = true;

      try {
        await CounselorApi.update(currentCounselorData.value._id, {
          channelId: formData.channelId,
          senderKey: formData.senderKey,
          receiveUrl: formData.receiveUrl,
          syncDkt: formData.syncDkt,
          status: formData.status,
        });

        message.success('상담톡 정보가 성공적으로 수정되었습니다.');
        state.modalVisible = false;
        loadCounselors();
      } catch (error) {
        handleError(error, '상담톡 정보 수정 중 오류가 발생했습니다.');
      } finally {
        state.modalLoading = false;
      }
    };

    // 모달 취소 처리
    const handleModalCancel = () => {
      state.modalVisible = false;
      currentCounselorData.value = {};
    };

    // 테이블 변경 핸들러
    const handleTableChange = (pag) => {
      if (pagination.current !== pag.current || pagination.pageSize !== pag.pageSize) {
        pagination.current = pag.current;
        pagination.pageSize = pag.pageSize;
        loadCounselors();
      }
    };

    // 검색 핸들러
    const handleSearch = () => {
      pagination.current = 1;
      loadCounselors();
    };

    // 조회 버튼 클릭 핸들러
    const handleRefresh = () => {
      loadCounselors();
      message.success('상담톡 목록을 새로고침했습니다.');
    };

    // 컴포넌트 마운트 시 데이터 로드
    onMounted(() => {
      loadCounselors();
    });

    return {
      // 상태관리
      state,
      searchParams,
      pagination,

      // 데이터
      counselorList,
      currentCounselorData,

      // 이벤트 핸들러
      handleChannelClick,
      handleModalOk,
      handleModalCancel,
      handleTableChange,
      handleSearch,
      handleRefresh,
    };
  }
};
</script>

<style scoped lang="scss">
.container-wrap {
  padding: 0 16px;
}

.titleContainer {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.page-title {
  color: #000;
  font-size: 1.8rem;
  margin-bottom: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.refresh-button {
  height: 36px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #333;

  &:hover {
    border-color: #FFCC32;
    color: #FFCC32;
  }

  &:focus {
    border-color: #FFCC32;
    box-shadow: 0 0 0 2px rgba(255, 204, 50, 0.2);
  }

  i {
    margin-right: 6px;
  }
}

.pageDesc {
  color: #6c757d;
  font-size: 14px;
  margin-top: 8px;
}
</style>
